import { createMemo, createUniqueId, For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { LogLine } from '../lib/fleet-live';

// The Command Deck's event log (#6778, prototype `:4097`): the 14 newest events, newest
// first, each a `clock()` time, a kind chip and a sentence (the model writes both). A line
// is kept by its key across patches, so only a line the reader has not seen slides in; the
// first paint slides nothing, and the slide sits inside `reduce-motion`.
//
// The log is a BOUNDED box (the canvas's height beside it, 15rem stacked under it), so the
// lines past its bottom SCROLL instead of being clipped out of reach (the prototype clips,
// brief.md item 67), and the scroller is a named tab stop so a keyboard can scroll it.

export function FleetLiveLog(props: {
  readonly lines: readonly LogLine[];
  /** The canvas's height when the log stands beside it, else `null` (stacked under it). */
  readonly height: number | null;
}): JSX.Element {
  let seen: Set<string> | null = null;
  const lines = createMemo(() => {
    const before = seen;
    seen = new Set(props.lines.map((l) => l.key));
    return props.lines.map((l) => ({ line: l, fresh: before !== null && !before.has(l.key) }));
  });
  const headingId = createUniqueId();
  return (
    <section
      class="fleet-live__log"
      style={props.height === null ? undefined : { height: `${props.height}px` }}
      aria-labelledby={headingId}
      tabindex="0"
    >
      <h4 id={headingId} class="fleet-live__log-heading">
        {t('fleet.live.log_heading')}
      </h4>
      <Show
        when={props.lines.length > 0}
        fallback={<p class="fleet-live__log-empty">{t('fleet.live.log_empty')}</p>}
      >
        <ul>
          <For each={lines()}>
            {(row) => (
              <li classList={{ 'fleet-live__log-line--new': row.fresh }}>
                <time datetime={row.line.at}>{row.line.time}</time>
                <span>
                  <span class={`fleet-live__chip fleet-live__chip--${row.line.tone}`}>
                    {row.line.chip}
                  </span>
                  {row.line.text}
                </span>
              </li>
            )}
          </For>
        </ul>
      </Show>
    </section>
  );
}
